import React, { useState, useEffect, useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useLocation } from "react-router-dom";
import FormInput from "../../form-components/InputComponent";
import Swal from "sweetalert2";
import { Block, DataTable, Icon, Button } from "../../../components/Component";
import checklistApis from "../../../api/checklist/checklistapi";
import QuestionsCreation, { Question } from "./QuestionsCreation";
import Content from "../../../layout/content/Content";
import { Skeleton } from "antd";
import { Row, Col } from "reactstrap";


interface FormData {
  checklistname: string;
}

const EditChecklist: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { checklistId } = location.state || {};
  const methods = useForm<FormData>();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [originalQuestions, setOriginalQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchChecklistData = async () => {
    try {
      if (!checklistId) {
        throw new Error("No checklist ID provided");
      }

      const response = await checklistApis.fetchChecklistById(checklistId);
      if (!response.data) {
        throw new Error("Failed to fetch checklist data");
      }

      const data = response.data;
      methods.reset({
        checklistname: data.checklistName || "",
      });

      // Transform questions
      const transformedQuestions = data.checklistQuestions?.map((q: any, index: number) => {
        let optionsString = '';
        if (q.optionsType) {
          optionsString = Object.values(q.optionsType).join(', ');
        }

        return {
          id: q?.questionId?.toString(),
          name: q.questionName,
          type: q.questionType,
          options: optionsString,
          mandatory: q.isMandatory,
          description: q.description || '',
        };
      }) || [];

      setQuestions(transformedQuestions);
      setOriginalQuestions(transformedQuestions);

    } catch (err) {
      console.error("Error loading checklist:", err);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load checklist data",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data when component opens
  useEffect(() => {
    fetchChecklistData();
  }, [checklistId, methods]);

  const handleQuestionsUpdate = useCallback((updatedQuestions: Question[]) => {
    setQuestions(updatedQuestions);
  }, []);

  const parseOptions = (optionsString: string) => {
    if (!optionsString) return undefined;
    const optionsArray = optionsString.split(',')
      .map(opt => opt.trim())
      .filter(opt => opt !== '');

    if (optionsArray.length === 0) return undefined;
    return optionsArray.reduce((acc, option, index) => {
      const key = String.fromCharCode(65 + index); // using A, B, C... for payload keys
      return { ...acc, [key]: option };
    }, {});
  };

  const handleSubmit = useCallback(async (data: FormData) => {
    try {
      setIsSubmitting(true);

      if (!checklistId) {
        throw new Error("Missing checklist ID");
      }

      const validQuestions = questions.filter(q => q && q.name && q.type);
      if (validQuestions.length === 0) {
        throw new Error("No valid questions found");
      }

      const newQuestions = validQuestions.filter(currentQuestion =>
        !originalQuestions.some(originalQuestion => originalQuestion.id === currentQuestion.id)
      );

      const deletedQuestions = originalQuestions
        .filter(originalQuestion =>
          !validQuestions.some(currentQuestion => currentQuestion.id === originalQuestion.id)
        )
        .map(q => ({ questionId: q.id }));

      const payload = {
        checklistId: checklistId,
        checklistName: data.checklistname.trim(),
        newLines: newQuestions.map(q => ({
          questionName: q.name,
          questionType: q.type.toLowerCase(),
          optionsType: q.options ? parseOptions(q.options) : undefined,
          isMandatory: q.mandatory,
          description: q.description || undefined
        })),
        deletedLines: deletedQuestions,
      };

      console.log("Payload being sent:", payload); // Debugging log

      const response = await checklistApis.updateChecklist(payload);

      if (!response || (response as any)?.status === false) {
        throw new Error((response as any).message || "Update failed");
      }

      Swal.fire({
        icon: "success",
        title: "Checklist Updated",
        text: "Your checklist has been updated.",
      }).then(() => {
        navigate('/pms/Checklist/checklist-lists');
      });
    } catch (error) {
      console.error("Error updating checklist:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error instanceof Error ? error.message : "Failed to update checklist. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [questions, originalQuestions, checklistId, navigate]);

  const confirmSubmit = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to update this checklist?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
      cancelButtonText: "No, cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        methods.handleSubmit(handleSubmit)();
      }
    });
  };

  const handleBack = () => {
    navigate("/pms/Checklist/checklist-lists");
  };


  if (isLoading) {
    return (
      <Content>
        <Block>
          <div className="card-inner">
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>
        </Block>
      </Content>
    );
  }

  return (
    <Content className="edit-modal-container font-regular">
      <Block>
        <FormProvider {...methods}>
          <DataTable
            title="Edit Checklist"
            className="no-border card-stretch fixed-width-table"
            style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}
            bodyClassName="border-none"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px', fontSize: '20px' }}>
              <h3 style={{ fontSize: '25px' }}> Edit Checklist</h3>
              <Button
                className="btn btn-primary btn-l"
                color="primary"
                size="sm"
                onClick={handleBack}
              >
                <Icon name="arrow-left"></Icon>
                <span>Back</span>
              </Button>
            </div>
            <form onSubmit={methods.handleSubmit(handleSubmit)}>
              <div className="card-inner position-relative" style={{ paddingTop: '5px' }}>
                <div className="row g-3">
                  <div className="col-5">
                    <FormInput
                      name="checklistname"
                      label="Checklist Title"
                      required={true}
                      type="text"
                      placeholder="Enter Checklist Name"
                    />
                  </div>
                </div>
              </div>
            </form>

            <QuestionsCreation
              initialQuestions={questions}
              onQuestionsUpdate={handleQuestionsUpdate}
              isLoading={isLoading}
            />

            <Row className="mt-4 mx-4 mb-5">
              <Col lg="12">
                <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                  <li>
                    <Button
                      color="primary"
                      className="btn-dim btn btn-secondary"
                      onClick={handleBack}
                    >
                      Cancel
                    </Button>
                  </li>
                  <li>
                    <Button
                      className="btn btn-primary btn-md"
                      type="button"
                      color="primary"
                      onClick={confirmSubmit}
                      disabled={isSubmitting || questions.length === 0}
                    >
                      {isSubmitting ? 'Updating...' : 'Update'}
                    </Button>
                  </li>
                </ul>
              </Col>
            </Row>
          </DataTable>
        </FormProvider>
      </Block>
    </Content>
  );
};

export default EditChecklist;